import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  readOpeningHours,
  removeSpecialPeriod,
  removeStandardHours,
  saveOpeningHours,
  type OpeningHoursWriteOutcome,
  type ReadOpeningHoursOutcome,
} from '@repo/front-end-services';
import type {
  AuthoredOpeningHoursDay,
  AuthoredOpeningHoursVersion,
  AuthoredOpeningHoursWrite,
  LaunchHours,
  OpeningHoursRead,
} from '@repo/frontend-types';
import type { DraftSaveStatus } from '../useDraftSaver';
import {
  calendarKey,
  groupOf,
  newSpecialBody,
  newStandardFromDate,
  sameDays,
  specialEnd,
  specialName,
  type StandardMove,
  standardNameStored,
  startPanelShows,
  startWeekBody,
  type StandardFields,
} from './hoursModel';

/**
 * The Opening hours step's page-level state (spec twin-details-page-hours-week, R3): its read, the
 * queue that saves each committed change straight to the twin, and where the saving stands for the
 * title bar's *Saved* tag. At page level, so a save already queued finishes even when the person
 * moves to another step.
 *
 * **One operation at a time, reads included.** A week is sent with the etag of the last answer to
 * this queue's own saves — never the one current when the change was made — so a run of quick changes
 * chains, and a save that raced another is refused `changed` rather than overwriting it. A newer
 * change to a calendar already waiting replaces the waiting one: the latest week wins.
 *
 * **An answer the queue did not ask for is never a licence to send.** A read, or an explicit
 * action's answer, can carry someone else's change, and a week queued before it was made against the
 * screen before it. So when one has landed since a week was queued, the week is checked against the
 * calendar as stored now before it goes: already stored → dropped quietly; still what the person was
 * editing (or one of their own attempts whose outcome was unknown) → sent, with the calendar's
 * current dates; anything else → dropped with *Someone else changed…*. A `changed` answer re-reads
 * before anything more is sent, and is settled the same way (review of this spec, 2026-10-17).
 *
 * **Every answer is adopted into the launch rule** (`useLaunchHours.adopt`), so the step's tick,
 * Launch and this step can never show different answers about whether the twin may launch.
 */

export type HoursRead =
  | { read: 'pending' }
  | { read: 'failed' }
  | { read: 'ready'; hours: OpeningHoursRead };

/** How an explicit action came out, for its popover. */
export type HoursActionResult =
  | { status: 'saved'; hours?: OpeningHoursRead }
  | { status: 'changed' }
  | { status: 'refused'; message: string }
  /** `unconfirmed`: the outcome was unknown and the re-read failed too, so whether it landed is not known. */
  | { status: 'failed'; unconfirmed?: true };

/** What an explicit action's popover says when it did not land: null when it did. */
export const actionProblem = (
  result: HoursActionResult,
  changed: string,
  unknown: string,
): string | null =>
  result.status === 'saved'
    ? null
    : result.status === 'refused'
      ? result.message
      : result.status === 'changed'
        ? changed
        : unknown;

export const HOURS_SAVE_FALLBACK_REASON = 'The opening hours could not be saved.';
export const HOURS_CHANGED_ELSEWHERE =
  'Someone else changed these opening hours, so your last change was not saved. They are shown as they are now.';
export const HOURS_STILL_SAVING = 'Saving your last change…';
/** A quick start refused because the screen was stale: hours already cover today or later. */
export const HOURS_ALREADY_SET =
  'This twin already has standard hours. They are shown as they are now.';

export interface UseOpeningHoursEditor {
  read: HoursRead;
  /** Whether a read can be made at all: there is a twin and a token. *Try again* only when true. */
  canRead: boolean;
  /** False before the twin exists (the create route has no `?twinBid=` yet). */
  hasTwin: boolean;
  /**
   * Counts the answers this editor did not ask for itself — reads, and explicit actions. A day whose
   * hours change across one changed under whoever was editing it (`DayRow` discards its drafts).
   */
  revision: number;
  /** The authored versions as on screen: the server's, with any week waiting or in flight laid over. */
  versions: AuthoredOpeningHoursVersion[];
  retryRead: () => void;
  /** Put a calendar's whole week on screen now, and save it. */
  saveCalendar: (calendar: AuthoredOpeningHoursVersion, days: AuthoredOpeningHoursDay[]) => void;
  /** *New Standard Hours From a Date*, with its name and its end (none: no end). */
  addFromDate: (fields: StandardFields) => Promise<HoursActionResult>;
  /**
   * *Edit details* on a running or upcoming standard (spec opening-hours-edit-standard-details, R3.5):
   * one replacement with its stored days, moving the sets next to it with it. `moves` is what the
   * step's note said the save moves (`standardEditNote`), so a week queued under a moved set follows it.
   */
  changeStandard: (
    calendar: AuthoredOpeningHoursVersion,
    fields: StandardFields,
    moves: readonly StandardMove[],
  ) => Promise<HoursActionResult>;
  /**
   * A quick start (spec twin-details-page-hours-start, R5): a standard week from the twin's today,
   * with no end. Only when nothing covers today or later.
   */
  startWith: (days: AuthoredOpeningHoursDay[]) => Promise<HoursActionResult>;
  /**
   * Another twin's hours, read with this editor's token — *Copy another twin*'s source. A read only:
   * nothing is ever written to that twin. Not queued: it touches nothing this editor holds.
   */
  readTwinHours: (bid: string, signal?: AbortSignal) => Promise<ReadOpeningHoursOutcome>;
  /** *Remove* on an upcoming standard week, handing its dates back. */
  removeUpcoming: (calendar: AuthoredOpeningHoursVersion) => Promise<HoursActionResult>;
  /** Only an upcoming standard, and only against a middleware that answers with `timezone` (R12). */
  canRemove: (calendar: AuthoredOpeningHoursVersion) => boolean;
  /**
   * Add a special period (spec twin-details-page-hours-special, R3.2): its week starts as a copy of
   * the standard covering its first day.
   */
  addSpecial: (period: SpecialPeriodFields) => Promise<HoursActionResult>;
  /** Rename or re-date a special period: one replacement, with its stored days. */
  changeSpecial: (
    calendar: AuthoredOpeningHoursVersion,
    period: SpecialPeriodFields,
  ) => Promise<HoursActionResult>;
  /** Delete a special period; the standard hours apply again on its dates. */
  removeSpecial: (calendar: AuthoredOpeningHoursVersion) => Promise<HoursActionResult>;
  /**
   * Only a special that has not started (ruling 1), so hours already past are never rewritten. No
   * `timezone` guard: nothing on the server changes, so there is no deploy window.
   */
  canDeleteSpecial: (calendar: AuthoredOpeningHoursVersion) => boolean;
  /** Anything waiting or in flight: the explicit actions wait. */
  busy: boolean;
  save: { status: DraftSaveStatus; reason: string | null; retry: () => void };
  /** The panel's error-tone note, or null. */
  notice: string | null;
  showNotice: (text: string | null) => void;
}

/** A special period's name and dates, as the card commits them. */
export interface SpecialPeriodFields {
  name: string;
  from: string;
  to: string;
}

/** Whether a stored version is the standard a write asked for: its start, end and name. */
const sameStandard = (
  v: AuthoredOpeningHoursVersion,
  want: Pick<AuthoredOpeningHoursVersion, 'name' | 'effectiveDateFrom' | 'effectiveDateTo'>,
) =>
  v.kind === 'standard' &&
  v.effectiveDateFrom === want.effectiveDateFrom &&
  v.effectiveDateTo === want.effectiveDateTo &&
  (v.name ?? null) === (want.name ?? null);

/**
 * The body that saves a calendar's week (R3.1): its own kind, its stored name (a standard's too, so a
 * day's change never clears it — spec opening-hours-edit-standard-details, R5.2) and dates. A stored special with no name or end (the store forbids both) is sent with *Special
 * period* and a one-day span, never with a null.
 */
const weekBody = (
  calendar: AuthoredOpeningHoursVersion,
  days: AuthoredOpeningHoursDay[],
  expectedEtag: string,
): AuthoredOpeningHoursWrite =>
  calendar.kind === 'special'
    ? {
        kind: 'special',
        name: specialName(calendar),
        effectiveDateFrom: calendar.effectiveDateFrom,
        effectiveDateTo: specialEnd(calendar),
        days,
        expectedEtag,
        replacesEffectiveDateFrom: calendar.effectiveDateFrom,
      }
    : {
        kind: 'standard',
        name: calendar.name ?? null,
        effectiveDateFrom: calendar.effectiveDateFrom,
        effectiveDateTo: calendar.effectiveDateTo,
        days,
        expectedEtag,
        replacesEffectiveDateFrom: calendar.effectiveDateFrom,
      };

interface Params {
  middlewareApi: string;
  accessToken: string | null;
  tokenFailed: boolean;
  twinBid: string | null;
  /** True while the Opening hours step is on screen: each arrival reads again. */
  active: boolean;
  adoptLaunchHours: (launchHours: LaunchHours) => void;
}

interface Pending {
  calendar: AuthoredOpeningHoursVersion;
  days: AuthoredOpeningHoursDay[];
  /** The calendar's stored days that this week was made against. */
  base: AuthoredOpeningHoursDay[];
  /** `foreignRef` when `base` was taken: any later outside answer means checking before sending. */
  seen: number;
}

const toResult = (o: OpeningHoursWriteOutcome): HoursActionResult =>
  o.status === 'refused'
    ? { status: 'refused', message: o.message }
    : o.status === 'saved'
      ? { status: 'saved', hours: o.hours }
      : { status: o.status };

export const useOpeningHoursEditor = ({
  middlewareApi,
  accessToken,
  tokenFailed,
  twinBid,
  active,
  adoptLaunchHours,
}: Params): UseOpeningHoursEditor => {
  const [read, setRead] = useState<HoursRead>({ read: 'pending' });
  const [overlay, setOverlay] = useState<ReadonlyMap<string, AuthoredOpeningHoursDay[]>>(new Map());
  const [saveStatus, setSaveStatus] = useState<DraftSaveStatus>('saved');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [revision, setRevision] = useState(0);

  /** Bumped by a twin change and unmount: an answer begun under an older one is dropped. */
  const genRef = useRef(0);
  const etagRef = useRef<string | null>(null);
  const hoursRef = useRef<OpeningHoursRead | null>(null);
  /** One operation at a time. */
  const runningRef = useRef(false);
  /** Weeks waiting to be sent, by calendar; Map order is the order they were first changed. */
  const waitingRef = useRef(new Map<string, Pending>());
  /** The week in flight. */
  const inFlightRef = useRef<({ key: string } & Pending) | null>(null);
  /** A failed save holds the queue until *Retry saving*. */
  const stalledRef = useRef(false);
  const readWantedRef = useRef(false);
  /** After a `changed`: nothing more is sent until a read says what is stored now. */
  const staleRef = useRef(false);
  /** Counts the answers this queue did not ask for — reads and explicit actions. */
  const foreignRef = useRef(0);
  /** Weeks sent per calendar whose outcome is unknown: if one is stored, it is the person's own. */
  const unknownRef = useRef(new Map<string, AuthoredOpeningHoursDay[][]>());
  const adoptRef = useRef(adoptLaunchHours);
  adoptRef.current = adoptLaunchHours;
  const twinBidRef = useRef(twinBid);
  twinBidRef.current = twinBid;

  const canRead = !!accessToken && !!twinBid && !tokenFailed;

  const refreshOverlay = useCallback(() => {
    const next = new Map<string, AuthoredOpeningHoursDay[]>();
    if (inFlightRef.current) next.set(inFlightRef.current.key, inFlightRef.current.days);
    Array.from(waitingRef.current).forEach(([key, p]) => next.set(key, p.days));
    setOverlay(next);
  }, []);

  const settleStatus = useCallback(() => {
    setSaveStatus(
      stalledRef.current
        ? 'failed'
        : inFlightRef.current || waitingRef.current.size > 0
          ? 'saving'
          : 'saved',
    );
    setBusy(runningRef.current || waitingRef.current.size > 0);
  }, []);

  /** BIDs already logged for a stored special with no end, so each is said once. */
  const loggedNoEndRef = useRef(new Set<string>());

  /** Take a whole answer: the hours, the etag, and the launch rule. */
  const applyHours = useCallback((hours: OpeningHoursRead) => {
    const bid = twinBidRef.current;
    if (
      bid &&
      !loggedNoEndRef.current.has(bid) &&
      hours.authored.versions.some(v => v.kind === 'special' && v.effectiveDateTo === null)
    ) {
      loggedNoEndRef.current.add(bid);
      // eslint-disable-next-line no-console
      console.error('[opening-hours] special without an end:', bid);
    }
    hoursRef.current = hours;
    etagRef.current = hours.authored.etag;
    setRead({ read: 'ready', hours });
    adoptRef.current(hours.launchHours);
  }, []);

  const dropQueue = useCallback(() => {
    waitingRef.current = new Map();
    inFlightRef.current = null;
    stalledRef.current = false;
    staleRef.current = false;
    unknownRef.current = new Map();
  }, []);

  const storedCalendar = useCallback(
    (key: string) => hoursRef.current?.authored.versions.find(v => calendarKey(v) === key) ?? null,
    [],
  );

  /**
   * Whether a waiting week may still be sent. Only asked when an outside answer has landed since it
   * was queued — otherwise the queue's own answers are the only change, and the week is current.
   */
  const verdictFor = useCallback(
    (key: string, p: Pending): 'send' | 'stored' | 'moved' => {
      if (p.seen === foreignRef.current) return 'send';
      const stored = storedCalendar(key);
      if (!stored) return 'moved';
      if (sameDays(stored.days, p.days)) return 'stored';
      const ours = [p.base, ...(unknownRef.current.get(key) ?? [])];
      return ours.some(d => sameDays(stored.days, d)) ? 'send' : 'moved';
    },
    [storedCalendar],
  );

  /** One read, as the running operation. Resolves to the fresh hours, or null. */
  const doRead = useCallback(async (): Promise<OpeningHoursRead | null> => {
    if (!accessToken || !twinBid) return null;
    const gen = genRef.current;
    let outcome;
    try {
      outcome = await readOpeningHours(middlewareApi, accessToken, twinBid);
    } catch {
      outcome = { status: 'failed' as const };
    }
    if (gen !== genRef.current) return null;
    if (outcome.status !== 'ready') {
      setRead({ read: 'failed' });
      return null;
    }
    applyHours(outcome.hours);
    foreignRef.current += 1;
    setRevision(foreignRef.current);
    staleRef.current = false;
    return outcome.hours;
  }, [accessToken, middlewareApi, twinBid, applyHours]);

  const pumpRef = useRef<() => void>(() => undefined);

  const runSave = useCallback(
    async (key: string, pending: Pending) => {
      if (!accessToken || !twinBid || etagRef.current === null) return;
      const gen = genRef.current;
      runningRef.current = true;
      waitingRef.current.delete(key);
      inFlightRef.current = { key, ...pending };
      refreshOverlay();
      settleStatus();
      // The calendar's dates as stored now: an explicit action may have moved them since.
      const calendar = storedCalendar(key) ?? pending.calendar;
      const { days } = pending;
      let outcome: OpeningHoursWriteOutcome;
      try {
        outcome = await saveOpeningHours(
          middlewareApi,
          accessToken,
          twinBid,
          weekBody(calendar, days, etagRef.current),
        );
      } catch {
        outcome = { status: 'failed' };
      }
      if (gen !== genRef.current) return;
      runningRef.current = false;
      inFlightRef.current = null;
      /** Put this calendar's latest week back first in line (a newer one waiting wins). */
      const requeue = () => {
        const rest = waitingRef.current;
        const latest = rest.get(key) ?? pending;
        rest.delete(key);
        waitingRef.current = new Map([[key, latest], ...Array.from(rest)]);
      };
      switch (outcome.status) {
        case 'saved': {
          applyHours(outcome.hours);
          unknownRef.current.delete(key);
          // A newer week for this calendar was made on top of the one just stored.
          const newer = waitingRef.current.get(key);
          const stored = storedCalendar(key);
          if (newer && stored) newer.base = stored.days;
          break;
        }
        case 'changed':
          // Someone else saved first — or our own earlier attempt had landed. Read before sending
          // anything more; the week is then sent, dropped quietly, or dropped with the note.
          requeue();
          staleRef.current = true;
          readWantedRef.current = true;
          break;
        case 'refused':
          // The refusal is about this calendar's week; other calendars' weeks still go.
          waitingRef.current.delete(key);
          unknownRef.current.delete(key);
          setNotice(outcome.message);
          readWantedRef.current = true;
          break;
        case 'failed':
          // The outcome is unknown. Keep the week, first in line, and hold until Retry: the etag makes
          // the retry safe, since a repeat of a save that landed is refused `changed`.
          unknownRef.current.set(key, [...(unknownRef.current.get(key) ?? []), days]);
          requeue();
          stalledRef.current = true;
          break;
      }
      refreshOverlay();
      settleStatus();
      pumpRef.current();
    },
    [accessToken, middlewareApi, twinBid, applyHours, refreshOverlay, settleStatus, storedCalendar],
  );

  const pump = useCallback(() => {
    if (runningRef.current || !canRead) return;
    // A wanted read goes first, so a week is never sent on a screen older than the store.
    if (readWantedRef.current) {
      readWantedRef.current = false;
      runningRef.current = true;
      settleStatus();
      const gen = genRef.current;
      void doRead().finally(() => {
        if (gen !== genRef.current) return;
        runningRef.current = false;
        settleStatus();
        pumpRef.current();
      });
      return;
    }
    if (staleRef.current || stalledRef.current || etagRef.current === null) return;
    for (;;) {
      const first = waitingRef.current.entries().next();
      if (first.done) return;
      const [key, pending] = first.value;
      const verdict = verdictFor(key, pending);
      if (verdict === 'send') {
        const stored = storedCalendar(key);
        if (stored) pending.base = stored.days;
        pending.seen = foreignRef.current;
        void runSave(key, pending);
        return;
      }
      waitingRef.current.delete(key);
      unknownRef.current.delete(key);
      if (verdict === 'moved') setNotice(HOURS_CHANGED_ELSEWHERE);
      refreshOverlay();
      settleStatus();
    }
  }, [canRead, doRead, runSave, settleStatus, refreshOverlay, storedCalendar, verdictFor]);
  pumpRef.current = pump;

  // A different twin, or no twin or token at all, starts again.
  useEffect(() => {
    genRef.current += 1;
    runningRef.current = false;
    dropQueue();
    readWantedRef.current = false;
    etagRef.current = null;
    hoursRef.current = null;
    setOverlay(new Map());
    setNotice(null);
    setSaveStatus('saved');
    setBusy(false);
    setRead(!twinBid || tokenFailed ? { read: 'failed' } : { read: 'pending' });
  }, [twinBid, tokenFailed, dropQueue]);

  // Each arrival on the step reads again — and the first one, once a token is here, and the first
  // for a different twin (the reset above has just cleared the last twin's hours).
  useEffect(() => {
    if (!active || !canRead) return;
    readWantedRef.current = true;
    pumpRef.current();
  }, [active, canRead, twinBid]);

  useEffect(
    () => () => {
      genRef.current += 1;
    },
    [],
  );

  const retryRead = useCallback(() => {
    if (!canRead) return;
    setRead(r => (r.read === 'ready' ? r : { read: 'pending' }));
    readWantedRef.current = true;
    pumpRef.current();
  }, [canRead]);

  const saveCalendar = useCallback(
    (calendar: AuthoredOpeningHoursVersion, days: AuthoredOpeningHoursDay[]) => {
      if (!hoursRef.current) return;
      const key = calendarKey(calendar);
      // A week already waiting or in flight for this calendar keeps its base: the stored days the
      // person's unsaved changes started from.
      const flying = inFlightRef.current?.key === key ? inFlightRef.current : null;
      const earlier = waitingRef.current.get(key) ?? flying;
      waitingRef.current.set(key, {
        calendar,
        days,
        base: earlier ? earlier.base : (storedCalendar(key)?.days ?? calendar.days),
        seen: earlier ? earlier.seen : foreignRef.current,
      });
      setNotice(null);
      refreshOverlay();
      settleStatus();
      pumpRef.current();
    },
    [refreshOverlay, settleStatus, storedCalendar],
  );

  const retrySave = useCallback(() => {
    if (!stalledRef.current) return;
    stalledRef.current = false;
    settleStatus();
    pumpRef.current();
  }, [settleStatus]);

  /**
   * An explicit action: only when nothing else is waiting or running, and never queued. `rekeys` are
   * the calendars the action moves to a new key (a special's new first day; an edited standard's new
   * start, and a next set moved with it): a week queued under an old key while it ran follows it, so
   * the person's own move never drops their edit as someone else's. All move at once, so one set
   * taking another's old key is never moved twice.
   */
  const runAction = useCallback(
    async (
      send: (etag: string) => Promise<OpeningHoursWriteOutcome>,
      landed: (fresh: OpeningHoursRead) => boolean,
      rekeys: ReadonlyArray<{ from: string; to: string }> = [],
    ): Promise<HoursActionResult> => {
      if (runningRef.current || waitingRef.current.size > 0 || etagRef.current === null) {
        return { status: 'refused', message: HOURS_STILL_SAVING };
      }
      if (!accessToken || !twinBid) return { status: 'failed' };
      const gen = genRef.current;
      runningRef.current = true;
      // Accepted for sending: an earlier action's problem no longer describes the hours.
      setNotice(null);
      settleStatus();
      let outcome: OpeningHoursWriteOutcome;
      try {
        outcome = await send(etagRef.current);
      } catch {
        outcome = { status: 'failed' };
      }
      if (gen !== genRef.current) return { status: 'failed' };
      let result = toResult(outcome);
      if (outcome.status === 'saved') {
        applyHours(outcome.hours);
        foreignRef.current += 1;
        setRevision(foreignRef.current);
      } else if (outcome.status === 'changed' || outcome.status === 'failed') {
        // Learn what is stored now. For an unknown outcome, the re-read says whether it landed.
        const fresh = await doRead();
        if (gen !== genRef.current) return { status: 'failed' };
        if (outcome.status === 'failed' && fresh && landed(fresh)) {
          result = { status: 'saved', hours: fresh };
        } else if (outcome.status === 'failed' && !fresh) {
          result = { status: 'failed', unconfirmed: true };
        }
      }
      const moving = rekeys.filter(r => r.from !== r.to);
      if (result.status === 'saved' && moving.length > 0) {
        const moved = (key: string) => moving.find(r => r.from === key)?.to ?? key;
        waitingRef.current = new Map(Array.from(waitingRef.current, ([key, p]) => [moved(key), p]));
        unknownRef.current = new Map(Array.from(unknownRef.current, ([key, u]) => [moved(key), u]));
        refreshOverlay();
      }
      runningRef.current = false;
      settleStatus();
      pumpRef.current();
      return result;
    },
    [accessToken, twinBid, applyHours, doRead, settleStatus, refreshOverlay],
  );

  const addFromDate = useCallback(
    ({ from, to, name }: StandardFields) => {
      const hours = hoursRef.current;
      if (!hours || !accessToken || !twinBid)
        return Promise.resolve<HoursActionResult>({ status: 'failed' });
      const { body } = newStandardFromDate(hours.authored.versions, { from, to, name });
      return runAction(
        etag =>
          saveOpeningHours(middlewareApi, accessToken, twinBid, { ...body, expectedEtag: etag }),
        fresh => fresh.authored.versions.some(v => sameStandard(v, body)),
      );
    },
    [accessToken, middlewareApi, twinBid, runAction],
  );

  const changeStandard = useCallback(
    (
      calendar: AuthoredOpeningHoursVersion,
      fields: StandardFields,
      moves: readonly StandardMove[],
    ) => {
      if (!hoursRef.current || !accessToken || !twinBid)
        return Promise.resolve<HoursActionResult>({ status: 'failed' });
      const name = standardNameStored(fields.name);
      return runAction(
        etag => {
          // The days as stored now: a week saved since the popover opened is kept, never put back.
          const stored = storedCalendar(calendarKey(calendar)) ?? calendar;
          return saveOpeningHours(middlewareApi, accessToken, twinBid, {
            kind: 'standard',
            name,
            effectiveDateFrom: fields.from,
            effectiveDateTo: fields.to,
            days: stored.days,
            expectedEtag: etag,
            replacesEffectiveDateFrom: calendar.effectiveDateFrom,
            movesAdjoiningStandards: true,
          });
        },
        fresh =>
          fresh.authored.versions.some(v =>
            sameStandard(v, { name, effectiveDateFrom: fields.from, effectiveDateTo: fields.to }),
          ),
        [
          {
            from: calendarKey(calendar),
            to: calendarKey({ kind: 'standard', effectiveDateFrom: fields.from }),
          },
          ...moves.map(m => ({
            from: calendarKey(m.version),
            to: calendarKey({ kind: 'standard', effectiveDateFrom: m.from }),
          })),
        ],
      );
    },
    [accessToken, middlewareApi, twinBid, runAction, storedCalendar],
  );

  const startWith = useCallback(
    (days: AuthoredOpeningHoursDay[]) => {
      const hours = hoursRef.current;
      if (!hours || !accessToken || !twinBid)
        return Promise.resolve<HoursActionResult>({ status: 'failed' });
      const today = hours.launchHours.today;
      // A stale screen: hours already cover today or later. Never send over them; read, and show
      // them as they are. The store's etag and overlap rules stay the authority (R5.2).
      if (today === null || !startPanelShows(hours.authored.versions, today)) {
        readWantedRef.current = true;
        pumpRef.current();
        return Promise.resolve<HoursActionResult>({
          status: 'refused',
          message: HOURS_ALREADY_SET,
        });
      }
      return runAction(
        etag =>
          saveOpeningHours(middlewareApi, accessToken, twinBid, {
            ...startWeekBody(today, days),
            expectedEtag: etag,
          }),
        fresh =>
          fresh.authored.versions.some(
            v => v.kind === 'standard' && v.effectiveDateFrom === today && sameDays(v.days, days),
          ),
      );
    },
    [accessToken, middlewareApi, twinBid, runAction],
  );

  const readTwinHours = useCallback(
    (bid: string, signal?: AbortSignal): Promise<ReadOpeningHoursOutcome> =>
      accessToken
        ? readOpeningHours(middlewareApi, accessToken, bid, signal)
        : Promise.resolve({ status: 'failed' }),
    [accessToken, middlewareApi],
  );

  const removeUpcoming = useCallback(
    (calendar: AuthoredOpeningHoursVersion) => {
      if (!accessToken || !twinBid) return Promise.resolve<HoursActionResult>({ status: 'failed' });
      return runAction(
        etag =>
          removeStandardHours(middlewareApi, accessToken, twinBid, {
            effectiveDateFrom: calendar.effectiveDateFrom,
            expectedEtag: etag,
          }),
        fresh => !fresh.authored.versions.some(v => calendarKey(v) === calendarKey(calendar)),
      );
    },
    [accessToken, middlewareApi, twinBid, runAction],
  );

  const canRemove = useCallback(
    (calendar: AuthoredOpeningHoursVersion) => {
      if (read.read !== 'ready') return false;
      const { hours } = read;
      const today = hours.launchHours.today;
      return (
        calendar.kind === 'standard' &&
        // The key's PRESENCE, never its value: an older middleware omits it and would ignore the
        // hand-back, leaving a gap (R12).
        'timezone' in hours &&
        today !== null &&
        groupOf(calendar, today) === 'upcoming'
      );
    },
    [read],
  );

  const addSpecial = useCallback(
    (period: SpecialPeriodFields) => {
      const hours = hoursRef.current;
      if (!hours || !accessToken || !twinBid)
        return Promise.resolve<HoursActionResult>({ status: 'failed' });
      const body = newSpecialBody(hours.authored.versions, period);
      return runAction(
        etag =>
          saveOpeningHours(middlewareApi, accessToken, twinBid, { ...body, expectedEtag: etag }),
        fresh =>
          fresh.authored.versions.some(
            v =>
              v.kind === 'special' &&
              v.effectiveDateFrom === body.effectiveDateFrom &&
              v.name === body.name &&
              v.effectiveDateTo === body.effectiveDateTo,
          ),
      );
    },
    [accessToken, middlewareApi, twinBid, runAction],
  );

  const changeSpecial = useCallback(
    (calendar: AuthoredOpeningHoursVersion, { name, from, to }: SpecialPeriodFields) => {
      if (!accessToken || !twinBid) return Promise.resolve<HoursActionResult>({ status: 'failed' });
      const trimmed = name.trim();
      return runAction(
        etag => {
          // The days as stored now: a week saved since the card opened is kept, never put back.
          const stored = storedCalendar(calendarKey(calendar)) ?? calendar;
          return saveOpeningHours(middlewareApi, accessToken, twinBid, {
            kind: 'special',
            name: trimmed,
            effectiveDateFrom: from,
            effectiveDateTo: to,
            days: stored.days,
            expectedEtag: etag,
            replacesEffectiveDateFrom: calendar.effectiveDateFrom,
          });
        },
        fresh =>
          fresh.authored.versions.some(
            v =>
              v.kind === 'special' &&
              v.effectiveDateFrom === from &&
              v.name === trimmed &&
              v.effectiveDateTo === to,
          ),
        [
          {
            from: calendarKey(calendar),
            to: calendarKey({ kind: 'special', effectiveDateFrom: from }),
          },
        ],
      );
    },
    [accessToken, middlewareApi, twinBid, runAction, storedCalendar],
  );

  const removeSpecial = useCallback(
    (calendar: AuthoredOpeningHoursVersion) => {
      if (!accessToken || !twinBid) return Promise.resolve<HoursActionResult>({ status: 'failed' });
      return runAction(
        etag =>
          removeSpecialPeriod(middlewareApi, accessToken, twinBid, {
            effectiveDateFrom: calendar.effectiveDateFrom,
            expectedEtag: etag,
          }),
        fresh => !fresh.authored.versions.some(v => calendarKey(v) === calendarKey(calendar)),
      );
    },
    [accessToken, middlewareApi, twinBid, runAction],
  );

  const canDeleteSpecial = useCallback(
    (calendar: AuthoredOpeningHoursVersion) => {
      if (read.read !== 'ready') return false;
      const today = read.hours.launchHours.today;
      return (
        calendar.kind === 'special' && today !== null && groupOf(calendar, today) === 'upcoming'
      );
    },
    [read],
  );

  const versions = useMemo(() => {
    if (read.read !== 'ready') return [];
    return read.hours.authored.versions.map(v => {
      const days = overlay.get(calendarKey(v));
      return days ? { ...v, days } : v;
    });
  }, [read, overlay]);

  return {
    read,
    canRead,
    hasTwin: !!twinBid,
    revision,
    versions,
    retryRead,
    saveCalendar,
    addFromDate,
    changeStandard,
    startWith,
    readTwinHours,
    removeUpcoming,
    canRemove,
    addSpecial,
    changeSpecial,
    removeSpecial,
    canDeleteSpecial,
    busy,
    save: {
      status: saveStatus,
      reason: saveStatus === 'failed' ? HOURS_SAVE_FALLBACK_REASON : null,
      retry: retrySave,
    },
    notice,
    showNotice: setNotice,
  };
};
